import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useConfig } from 'dashboard/composables/useConfig';
import { usePolicy } from 'dashboard/composables/usePolicy';
import FunnelAPI from 'dashboard/api/funnel';
import {
  getSettingsCenterCatalog,
  getSettingsCenterGroups,
} from './settingsCatalog';

// Funil pedido pelo link (funnel_id) ou, se ele nao existe mais, o primeiro
// disponivel. Antes o link antigo deixava todos os cards de funil travados, sem
// dizer por que; agora a tela abre num funil valido e avisa da troca.
export const pickFunnelId = (funnels, requestedId) => {
  const requested = String(requestedId || '');
  const found = funnels.find(funnel => String(funnel.id) === requested);
  return found?.id || funnels[0]?.id || null;
};

export const useSettingsCenter = () => {
  const route = useRoute();
  const router = useRouter();
  const accountId = computed(() => String(route.params.accountId || ''));

  const { t } = useI18n();
  const { isStacklab } = useConfig();
  const { checkPermissions, checkInstallationType, isFeatureFlagEnabled } =
    usePolicy();
  const role = useMapGetter('getCurrentRole');
  const customRoleId = useMapGetter('getCurrentCustomRoleId');
  const funnels = ref([]);
  const isLoadingFunnels = ref(false);
  const query = ref('');
  const error = ref(null);

  const isAdmin = computed(
    () => role.value === 'administrator' && customRoleId.value === null
  );
  const requestedFunnelId = computed(() =>
    String(route.query?.funnel_id || '')
  );
  const invalidFunnelId = computed(
    () =>
      Boolean(requestedFunnelId.value) &&
      !funnels.value.some(
        funnel => String(funnel.id) === requestedFunnelId.value
      )
  );
  const selectedFunnelId = computed(() =>
    pickFunnelId(funnels.value, requestedFunnelId.value)
  );

  const canUseKanban = computed(
    () =>
      checkPermissions([
        'administrator',
        'agent',
        'kanban_manage',
        'kanban_view',
      ]) && isFeatureFlagEnabled('kanban_board')
  );

  const area = computed(() => String(route.query?.config_area || ''));
  const catalog = computed(() =>
    getSettingsCenterCatalog({
      isAdmin: isAdmin.value,
      isStacklab,
      isFeatureEnabled: isFeatureFlagEnabled,
      checkPermissions,
      checkInstallationType,
      translate: t,
      query: query.value,
      area: query.value ? '' : area.value,
      router,
      params: route.params,
    })
  );
  const groups = computed(() =>
    getSettingsCenterGroups({ items: catalog.value, translate: t })
  );

  const loadFunnels = async () => {
    if (!canUseKanban.value || !accountId.value) {
      isLoadingFunnels.value = false;
      return;
    }
    error.value = null;
    const requestedAccountId = accountId.value;
    isLoadingFunnels.value = true;
    try {
      const { data } = await FunnelAPI.get();
      if (requestedAccountId !== accountId.value) return;
      funnels.value = Array.isArray(data) ? data : [];
    } catch (cause) {
      if (requestedAccountId !== accountId.value) return;
      error.value = cause;
      funnels.value = [];
    } finally {
      if (requestedAccountId === accountId.value)
        isLoadingFunnels.value = false;
    }
  };

  watch(
    [accountId, canUseKanban],
    () => {
      funnels.value = [];
      loadFunnels();
    },
    { immediate: true }
  );

  return {
    area,
    catalog,
    error,
    funnels,
    groups,
    isAdmin,
    isLoadingFunnels,
    invalidFunnelId,
    loadFunnels,
    query,
    route,
    router,
    selectedFunnelId,
  };
};
